export type JobAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

export function jobAddressLines(address: JobAddress): string[] {
  const clean = (value?: string | null) => String(value ?? '').trim();
  if (![address.line1, address.line2, address.city, address.state, address.postalCode].some(value => clean(value))) return [];
  const statePostal = [clean(address.state), clean(address.postalCode)].filter(Boolean).join(' ');
  return [clean(address.line1), clean(address.line2), [clean(address.city), statePostal].filter(Boolean).join(', '), clean(address.country)]
    .filter(Boolean);
}

export type MapsApp = 'apple' | 'default' | 'google';

// Each device opens its own maps app. iPhones and iPads get Apple Maps; iPadOS can report a Mac user
// agent, so touch support tells them apart. Android hands a geo: link to the maps app the person chose.
// Computers have no maps app, so they get Google Maps.
export function deviceMapsApp(device: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> | undefined = typeof navigator === 'undefined' ? undefined : navigator): MapsApp {
  const agent = device?.userAgent ?? '';
  if (/iPhone|iPad|iPod/.test(agent) || (/Macintosh/.test(agent) && (device?.maxTouchPoints ?? 0) > 1)) return 'apple';
  if (/Android/.test(agent)) return 'default';
  return 'google';
}

export function jobDirectionsUrl(address: string, app: MapsApp = deviceMapsApp()): string | null {
  const destination = address.trim();
  if (!destination) return null;
  const encoded = encodeURIComponent(destination);
  if (app === 'apple') return `https://maps.apple.com/?daddr=${encoded}`;
  // Opens the place rather than a route; the maps app offers directions from there.
  if (app === 'default') return `geo:0,0?q=${encoded}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
}
